import { sumBy } from 'lodash';
import { usePaginatedFilters } from 'further-ui/hooks';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';
import { getQueryKey } from '@trpc/react-query';

export default function useOnboardingOverview(
  pageId: string,
  vehicleId: string | null,
) {
  const { filters, handleChangeFilters, pagination } = usePaginatedFilters(
    pageId,
    {
      defaultFilters: {
        search: '',
        showClosed: false,
      },
      defaultOrder: {
        orderBy: 'investorName',
        order: 'asc',
      },
      orderByOptions: [
        'investorName',
        'resolvedCommitmentAmount',
        'completedStepsWeight',
      ],
    },
  );

  const { data, isFetching, isRefetching, refetch } =
    api.onboarding.listInvestors.useQuery(
      {
        vehicleId: vehicleId ?? '',
        ...pagination.properties,
        search: filters.search,
        showClosed: filters.showClosed,
      },
      { enabled: !!vehicleId },
    );

  const commitmentStats = api.onboarding.getCommitmentStats.useQuery(
    { vehicleId: vehicleId ?? '' },
    { enabled: !!vehicleId },
  );

  const { data: equalisationsData } = api.equalisations.list.useQuery(
    { vehicleId: vehicleId ?? '' },
    { enabled: !!vehicleId },
  );

  const hasClosedCommitments =
    equalisationsData?.results.some((equalisation) => !equalisation.isDraft) ??
    false;

  const refetchInvestors = () => {
    void refetch();
    void commitmentStats.refetch();
  };

  const { mutate: removeInvestor, isPending: isRemovingInvestor } =
    api.onboarding.removeInvestor.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor removed successfully.',
        invalidations: [
          getQueryKey(api.onboarding.getCommitmentStats),
          getQueryKey(api.onboarding.listInvestors),
        ],
      }),
    );

  const { updateOnboardingSettings, isUpdating: isUpdatingOnboardingSettings } =
    useOnboardingSettings(vehicleId ?? '');

  const prospectiveCommitmentsValue = sumBy(
    commitmentStats.data ?? [],
    'commitmentAmount',
  );
  const signedCommitmentsValue = sumBy(
    (commitmentStats.data ?? []).filter((commitment) => commitment.isSigned),
    'commitmentAmount',
  );

  const handleRemoveInvestor = (vehicleApplicationId: string) => {
    if (!vehicleId || !vehicleApplicationId) {
      return;
    }

    removeInvestor({
      vehicleApplicationId,
      vehicleId,
    });
  };

  const { mutate: deleteCommitment } = api.commitments.delete.useMutation(
    useHandleTrpcMutation()({}),
  );

  const handleDeleteCommitment = (commitmentId: string) => {
    if (!vehicleId || !commitmentId) {
      return;
    }

    deleteCommitment({
      commitmentId,
      vehicleId,
    });
  };

  return {
    commitmentStats: commitmentStats.data ?? [],
    filters,
    handleChangeFilters,
    pagination,
    signedCommitmentsValue,
    prospectiveCommitmentsValue,
    isFetching,
    isRefetching,
    investors: data?.results ?? [],
    totalInvestors: data?.totalCount ?? 0,
    handleDeleteCommitment,
    handleRemoveInvestor,
    isRemovingInvestor,
    updateOnboardingSettings,
    isUpdatingOnboardingSettings,
    refetchInvestors,
    hasClosedCommitments,
  };
}
